import { SESSIONS_TEST_IDS } from '@/components/admin/sessions/contracts';
import { usePrefersReducedMotion } from '@/components/reader/use-reduced-motion';
import {
  type SessionTocTrials,
  TOC_TITLE,
  type TocEntry,
  sessionTocEntries,
} from '@/lib/session-toc';
import { cn } from '@/lib/utils';
import type { SessionDetail } from '@matter_extractor/shared';
import { type MouseEvent, useEffect, useMemo, useState } from 'react';

/**
 * The SOMMAIRE of the Parcours tab (refonte-modulaire.md §12.2 « Depuis #221 »; maquette
 * `<nav class="toc">` of `Vue de session`): a column to the right of the walk that lists what the
 * screen holds - the pages in the order traversed, the protocol block and what it launched, the
 * two sections of the blank trials - so a long session is crossed by its names and not by the
 * scrollbar.
 *
 * ## `xl` and up, and nothing below
 *
 * The column is 240 px and the walk keeps the rest: at 1280 px the content of `<main>` is 1132 px
 * (1180 less its gutters, the 56 px rail already out), which leaves the cards 860 px. Under `xl`
 * the same column would take the walk below ~650 px (at `lg`, 920 px of content), where a card
 * with its chain of steps and its six notes starts to wrap on itself; and the maquette's answer
 * for the narrow case - a row of buttons above the walk - is what the decision of 2026-10-29
 * dropped: « pas de sommaire sur téléphone ni tablette, pas de version repliée ». `hidden xl:block`
 * takes it out of the layout AND of the accessibility tree, so no narrow screen reads a list of
 * links to a page it can already scroll.
 *
 * ## How it is laid out: reserved on the right, drawn in the reserve
 *
 * The root of `SessionReading` reserves `TOC_RESERVE` of padding on its right from `xl` (on the
 * Parcours tab only), and this component stands in that reserve with `absolute inset-y-0 right-0`:
 * a column as TALL as the tab, inside which the `nav` is `sticky` under the administration bar.
 * A grid around the tab's content would have re-indented every block the tab holds, and #219/#220
 * move those blocks; this keeps the sommaire one element and one class away from them.
 *
 * ## A link that scrolls, and says so
 *
 * Each entry is an `<a href="#anchor">`, so it is a link for the keyboard, the context menu and a
 * screen reader - but a plain click is taken over: `scrollIntoView`, and no change of the
 * address. A native fragment would write a history entry per click (the back button then walks the
 * sections instead of leaving the session) and hand the URL to the router, whose `?tab=` it must
 * keep. `scroll-padding-top` of `styles.css` parks the target under the administration bar, as for
 * every other jump of the app; the motion is smooth unless the person asked for less.
 *
 * ## The section in view is marked `aria-current="location"`
 *
 * An `IntersectionObserver` watches the targets through a band that starts under the bar and ends
 * before the middle of the window; the entry marked is the FIRST of the list whose target
 * intersects the band, and when none does (a gap between two blocks) the previous mark stays. A
 * click marks its entry at once, before the scroll has settled. jsdom has no observer, so the
 * effect gives up when it is missing, as `editor-header` does: the tests that want the mark pose
 * a fake one (`tests/sessions-sommaire.test.tsx`).
 */

/**
 * What the root of `SessionReading` reserves on its right from `xl`: the column (15 rem) and a
 * gutter of 2 rem. A literal, so that Tailwind finds the class where it is written.
 */
export const TOC_RESERVE = 'xl:pr-[17rem]';

/**
 * The band an entry counts as « in view » in: 64 px under the top (the 48 px bar and a 16 px
 * margin, the sticky offset of the column) down to 40 % of the window. `rootMargin` takes pixels
 * and percentages and no `calc`, so the bar's height is written out here; `admin-bar-offset.test.ts`
 * is what holds the token.
 */
const IN_VIEW_BAND = '-64px 0px -60% 0px';

export interface SessionTocProps {
  readonly detail: SessionDetail;
  /**
   * Which sections of the blank trials the route mounts under the fil (#224): only the ones the
   * session has no block for. The route knows - a protocol was played, not the vitrine.
   */
  readonly trials: SessionTocTrials;
}

export function SessionToc({ detail, trials }: SessionTocProps) {
  const { noyau, rangement } = trials;
  const entries = useMemo(
    () => sessionTocEntries(detail, { trials: { noyau, rangement } }),
    [detail, noyau, rangement],
  );
  const reduced = usePrefersReducedMotion();
  const [current, setCurrent] = useState<string | null>(null);
  const anchors = entries.map((entry) => entry.anchor).join('|');

  useEffect(() => {
    // Optional: jsdom has no `IntersectionObserver`, and an effect that threw would take the tab down.
    if (typeof IntersectionObserver === 'undefined') return;
    const order = anchors === '' ? [] : anchors.split('|');
    const inBand = new Set<string>();
    const observer = new IntersectionObserver(
      (records) => {
        for (const record of records) {
          if (record.isIntersecting) inBand.add(record.target.id);
          else inBand.delete(record.target.id);
        }
        const first = order.find((anchor) => inBand.has(anchor));
        if (first !== undefined) setCurrent(first);
      },
      { rootMargin: IN_VIEW_BAND },
    );
    for (const anchor of order) {
      const target = document.getElementById(anchor);
      if (target !== null) observer.observe(target);
    }
    return () => observer.disconnect();
  }, [anchors]);

  const go = (event: MouseEvent<HTMLAnchorElement>, anchor: string): void => {
    // A modified click keeps its native meaning (a new tab on the address).
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }
    const target = document.getElementById(anchor);
    if (target === null) return;
    event.preventDefault();
    // Optional call: jsdom has no `scrollIntoView`.
    target.scrollIntoView?.({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
    setCurrent(anchor);
  };

  return (
    <div className="absolute inset-y-0 right-0 hidden w-60 xl:block">
      <nav
        aria-label="Sommaire de la session"
        data-testid={SESSIONS_TEST_IDS.walkToc}
        data-count={entries.length}
        className="sticky top-[calc(var(--spacing-admin-bar)+1rem)] flex max-h-[calc(100dvh-var(--spacing-admin-bar)-2rem)] flex-col gap-1 overflow-y-auto rounded-md border border-gray-200 bg-white p-3"
      >
        <p className="px-2 pb-1.5 font-semibold text-gray-600 text-micro uppercase tracking-wide">
          {TOC_TITLE}
        </p>
        <ol className="flex flex-col gap-0.5">
          {entries.map((entry) => (
            <li key={entry.anchor}>
              <a
                href={`#${entry.anchor}`}
                data-testid={SESSIONS_TEST_IDS.walkTocEntry}
                data-anchor={entry.anchor}
                data-kind={entry.kind}
                aria-current={current === entry.anchor ? 'location' : undefined}
                onClick={(event) => go(event, entry.anchor)}
                className={cn(
                  'flex items-baseline gap-2 rounded-md border-transparent border-l-2 px-2 py-1.5 text-caption text-gray-900 no-underline hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit',
                  // Not by colour alone: the mark in view is also bolder and carries a bar.
                  'aria-[current=location]:border-bleu-nuit aria-[current=location]:bg-bleu-nuit-tint aria-[current=location]:font-semibold',
                  entryClass(entry),
                )}
              >
                {entry.rank === null ? null : (
                  <span className="min-w-4 text-gray-500 tabular-nums">{entry.rank}</span>
                )}
                <span className="min-w-0 break-words">{entry.label}</span>
              </a>
            </li>
          ))}
        </ol>
      </nav>
    </div>
  );
}

/** The maquette's three weights: the protocol in bleu nuit, what it launched indented, the rest plain. */
function entryClass(entry: TocEntry): string {
  switch (entry.kind) {
    case 'protocol':
      return 'font-bold text-bleu-nuit';
    case 'block':
      return 'pl-6 text-gray-700';
    case 'section':
    case 'page':
      return '';
  }
}
